import { createTheme } from '@mui/material/styles';

// Typography settings (same as light theme)
const typography = {
  fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  h1: {
    fontWeight: 700,
    fontSize: '2.5rem',
  },
  h2: {
    fontWeight: 700,
    fontSize: '2rem',
  },
  h3: {
    fontWeight: 600,
    fontSize: '1.75rem',
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.5rem',
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.25rem',
  },
  h6: {
    fontWeight: 600,
    fontSize: '1rem',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
  },
  button: {
    fontWeight: 500,
    textTransform: 'none',
  },
};

// Shape definitions
const shapes = {
  button: {
    small: '4px',
    medium: '8px',
    large: '30px',
    pill: '50px',
  },
  card: {
    small: '8px',
    medium: '12px',
    large: '16px',
    default: '12px',
  },
  input: {
    small: '8px',
    medium: '10px',
    large: '12px',
  },
};

// Dark mode color palette
const darkColors = {
  primary: {
    main: '#00BCD4',
    light: '#4DD0E1',
    dark: '#0097A7',
    contrastText: '#FFFFFF',
  },
  // Secondary colors - Aspiration Orange for action buttons
  secondary: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#ffffff',
  },
  success: {
    main: '#81c784',
    light: '#a5d6a7',
    dark: '#66bb6a',
    contrastText: '#000000',
  },
  error: {
    main: '#e57373',
    light: '#ef9a9a',
    dark: '#ef5350',
    contrastText: '#000000',
  },
  warning: {
    main: '#ffb74d',
    light: '#ffcc80',
    dark: '#ffa726',
    contrastText: '#000000',
  },
  info: {
    main: '#00BCD4',
    light: '#4DD0E1',
    dark: '#0097A7',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#121212',
    paper: '#1e1e1e',
    subtle: '#1e1e1e',
  },
  table: {
    headerBg: '#2d2d2d',
    headerText: 'rgba(255, 255, 255, 0.87)',
    rowHover: 'rgba(255, 255, 255, 0.05)',
    rowOdd: 'rgba(255, 255, 255, 0.02)',
    border: 'rgba(255, 255, 255, 0.12)',
  },
  text: {
    primary: 'rgba(255, 255, 255, 0.87)',
    secondary: 'rgba(255, 255, 255, 0.6)',
    disabled: 'rgba(255, 255, 255, 0.38)',
  },
  scrollbar: {
    track: '#2a2a2a',
    thumb: '#555',
    thumbHover: '#777',
  },
};

// Create dark theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: darkColors.primary,
    secondary: darkColors.secondary,
    success: darkColors.success,
    error: darkColors.error,
    warning: darkColors.warning,
    info: darkColors.info,
    background: darkColors.background,
    text: darkColors.text,
  },
  typography,
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: shapes.button.medium,
          fontWeight: typography.button.fontWeight,
          textTransform: typography.button.textTransform,
          padding: '8px 16px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          },
        },
        containedPrimary: {
          backgroundColor: darkColors.primary.main,
          '&:hover': {
            backgroundColor: darkColors.primary.dark,
          },
        },
        containedSecondary: {
          backgroundColor: darkColors.secondary.main,
          '&:hover': {
            backgroundColor: darkColors.secondary.dark,
          },
        },
        sizeLarge: {
          borderRadius: shapes.button.large,
          padding: '12px 32px',
          fontSize: '1rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: shapes.card.default,
          boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: shapes.input.medium,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.primary,
          fontWeight: 600,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
        body: {
          fontSize: '14px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: darkColors.table.rowOdd,
          },
          '&:hover': {
            backgroundColor: darkColors.table.rowHover,
          },
          '&:last-child td, &:last-child th': {
            border: 0,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: shapes.card.default,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: shapes.button.small,
        },
      },
    },
    // CardHeader: slightly lifted strip vs card body (paper) for hierarchy
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.subtle,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '& .MuiCardHeader-title': {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    // Select component overrides - Modern styling
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiSelect-select': {
            padding: '14px 40px 14px 14px',
            textAlign: 'center',
            minHeight: '20px',
            lineHeight: '20px',
          },
          '& .MuiSelect-icon': {
            right: '12px',
            color: 'rgba(255, 255, 255, 0.87)'
          }
        }
      }
    },
    // MenuItem component overrides - For Select dropdowns
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&.Mui-selected': {
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.main',
            }
          }
        }
      }
    },
    // FormControl component overrides
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: darkColors.background.paper,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            },
            '&.Mui-focused': {
              boxShadow: '0 4px 12px rgba(100, 181, 246, 0.3)',
            },
            '& .MuiSelect-select': {
              borderRadius: 8,
            }
          }
        }
      }
    },
    // Menu component overrides
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          border: '1px solid',
          borderColor: 'divider',
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: darkColors.primary.main,
          color: darkColors.primary.contrastText,
        },
      },
    },
  },
});
